import { createContext, ReactNode, useContext } from "react";

import { Job } from "@/types";

interface SheetScopeContextValue {
  candidacyId: string;
  companyId: string;
  jobId: string;
  // Optional so id-only providers (e.g. MyAviary outer wrap) still satisfy the type.
  companyName?: string;
  job?: Job;
  // Compact mode hides chrome (action buttons, score dropdown, ellipsis) for the dashboard review modal.
  isCompact?: boolean;
}

// Exported for the few hooks that need optional access; most consumers use useSheetScope.
export const SheetScopeContext = createContext<
  SheetScopeContextValue | undefined
>(undefined);

export const SheetScopeProvider = ({
  candidacyId,
  children,
  companyId,
  companyName,
  isCompact,
  job,
  jobId,
}: SheetScopeContextValue & { children: ReactNode }) => {
  return (
    <SheetScopeContext.Provider
      value={{ candidacyId, companyId, companyName, isCompact, job, jobId }}
    >
      {children}
    </SheetScopeContext.Provider>
  );
};

// Throws if not inside a fully-populated SheetScopeProvider.
export const useSheetScope = () => {
  const context = useContext(SheetScopeContext);
  if (!context?.job || !context.companyName) {
    throw new Error(
      "useSheetScope requires a SheetScopeProvider with `job` and `companyName`",
    );
  }
  return context as Required<SheetScopeContextValue>;
};
